import React from 'react'
import { Box, CircularProgress } from '@mui/material'
import { INTERAX_COLORS } from '../constants/colors'

export interface SpinnerProps {
  /** Size of the spinner */
  size?: 'small' | 'medium' | 'large'
  /** Color of the spinner */
  color?: 'primary' | 'secondary' | 'white' | 'sun'
  /** Whether to show the spinner */
  loading?: boolean
  /** Text to display below the spinner */
  text?: string
  /** Additional CSS classes */
  className?: string
  /** Whether to center the spinner */
  centered?: boolean
  /** Variant of the spinner display */
  variant?: 'inline' | 'fullPage' | 'overlay'
}

/**
 * Spinner Component
 *
 * A design system spinner component built on Material UI CircularProgress with Interax styling.
 * Supports different sizes, colors, optional text, and multiple display variants.
 *
 * @param props - Props for the Spinner component
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = 'medium',
  color = 'primary',
  loading = true,
  text,
  className = '',
  centered = false,
  variant = 'inline'
}) => {
  if (!loading) return null

  const getSize = () => {
    switch (size) {
      case 'small':
        return 20
      case 'large':
        return 48
      default:
        return 32
    }
  }

  const getColor = () => {
    switch (color) {
      case 'secondary':
        return INTERAX_COLORS.thunderGrey
      case 'white':
        return INTERAX_COLORS.white
      case 'sun':
        return INTERAX_COLORS.sun
      default:
        return INTERAX_COLORS.sun
    }
  }

  const spinnerElement = (
    <Box className={className} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
      <CircularProgress
        size={getSize()}
        sx={{
          color: getColor()
        }}
      />
      {text && (
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, textAlign: 'center' }}>
          {text}
        </Box>
      )}
    </Box>
  )

  // Full page variant
  if (variant === 'fullPage') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: INTERAX_COLORS.offBlack,
          gap: 2
        }}
      >
        <CircularProgress
          size={getSize()}
          sx={{
            color: getColor()
          }}
        />
        {text && (
          <Box
            component="p"
            sx={{
              color: INTERAX_COLORS.thunderGrey,
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    )
  }

  // Overlay variant
  if (variant === 'overlay') {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          backdropFilter: 'blur(2px)'
        }}
      >
        <Box
          sx={{
            backgroundColor: INTERAX_COLORS.white,
            borderRadius: 2,
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <CircularProgress
            size={getSize()}
            sx={{
              color: getColor()
            }}
          />
          {text && (
            <Box
              sx={{
                color: INTERAX_COLORS.offBlack,
                fontSize: '16px',
                fontWeight: 500
              }}
            >
              {text}
            </Box>
          )}
        </Box>
      </Box>
    )
  }

  // Inline variant (default)
  if (centered) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        {spinnerElement}
      </Box>
    )
  }

  return spinnerElement
}

export default Spinner
